import Vue, { PropType } from "vue";
import format from "date-fns/format";
import { Novel, TCopyNovelPayload } from "@/lib/models";
import NovelDeleteConfirm, { NovelDeleteConfirmProps } from "@/components/ui/novel/NovelDeleteConfirm.vue";
import { Dialog } from "@/lib/utils";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/dialogs/SimpleDialog.vue";
import CopyNovelDialog from "@/components/ui/CopyNovelDialog.vue";
import NovelGroupingDialog from "@/components/ui/dialogs/NovelGroupingDialog.vue";
import {
  filterSupportedManuscriptFiles,
  openManuscriptFilePicker,
  readManuscriptFiles,
} from "@/lib/utils/manuscriptFileImport";
import { getTranslatedWorkStatusById } from "@/lib/utils/novelSettingsHelper";

interface Props {
  novel: Novel;
  isMenuOpen: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Data {}

interface Computed {
  toUpdatedAt: string;
  toCreatedAt: string;
  getImageKey?: string;
  coverImageVersion: number;
  workStatusLabel: string;
  novelIdFromNolaNovel?: string;
}

interface Methods {
  onClickPinned(): void;
  showCopyNovelDialog(): void;
  copyNovel(copySelectedData: [] | string[]): void;
  importNovel(): void;
  deleteNovel(): void;
  showNovelDeleteConfirmDialog(title: string): void;
  showGroupingDialog(): void;
  toggleMenu(): void;
  onSelectFile(): Promise<void>;
  createMultipleManuscript(payload: { title?: string; content?: string }[]): Promise<void>;
}

const novelItemMixin = Vue.extend<Data, Methods, Computed, Props>({
  props: {
    novel: {
      type: Object as PropType<Novel>,
      required: true,
    },
    isMenuOpen: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {};
  },
  computed: {
    toUpdatedAt() {
      const { updatedAtLatestDataInNovel } = this.novel;
      if (updatedAtLatestDataInNovel) return format(updatedAtLatestDataInNovel, "YYYY/MM/DD");
      return "-";
    },
    toCreatedAt() {
      const { createdAt } = this.novel;
      if (createdAt) return format(createdAt, "YYYY/MM/DD");
      return "-";
    },
    getImageKey() {
      const { novelId, image } = this.novel;
      if (image && image.startsWith("file:")) {
        return `file:novels/${novelId}/cover.jpg`;
      }
      return image;
    },
    // imageの値が"file:"始まりだと常に同じURLに解決されるため、表紙変更後に一覧のサムネイルが
    // 古いキャッシュ画像のまま残らないよう、詳細ページ・サイドメニューと同様keyを変えて強制再マウントする
    coverImageVersion() {
      return this.$store.getters["novelModule/coverImageVersion"](this.novel.novelId);
    },
    workStatusLabel() {
      const workStatus = this.novel.workStatus || "inProgress";
      const t = (key: string) => this.$t(key) as string;
      return getTranslatedWorkStatusById(t, workStatus);
    },
    novelIdFromNolaNovel() {
      return this.novel?.associatedData?.nolaNovel?.id || undefined;
    },
  },
  methods: {
    async onClickPinned() {
      const { $store, novel } = this;
      await $store.dispatch("novelModule/updateNovel", {
        novelId: novel.novelId,
        pinned: !novel.pinned,
      });
    },
    async showCopyNovelDialog() {
      const { novel } = this;
      this.toggleMenu();
      const copyNovelDialog = new Dialog(CopyNovelDialog);
      const result = await copyNovelDialog.show({ novelId: novel.novelId });
      if (result) {
        this.copyNovel(result);
      }
    },
    async copyNovel(copySelectedData: [] | string[]) {
      this.$emit("changeLoading", true);
      const { $store, novel } = this;
      try {
        const payload: TCopyNovelPayload = { novelId: novel.novelId };
        if (copySelectedData.length > 0) {
          payload.copySelectedData = copySelectedData;
        }
        const success = await $store.dispatch("novelModule/copyNovel", payload);
        if (success) {
          const completeDialog = new Dialog(SimpleDialog);
          const completeData: SimpleDialogProps = {
            title: "作品の複製が完了しました",
            content: `<span>選択した作品を「${novel.title}のコピー」として複製されました。</span><br /><span>該当の作品を開いて確認をお願いいたします。</span>`,
            width: 380,
          };
          completeDialog.show(completeData);
          $store.dispatch("novelModule/initialize");
          $store.dispatch("memoModule/initialize");
        } else {
          throw new Error("Copy novel operation returned false");
        }
      } catch (error) {
        const errorDialog = new Dialog(SimpleDialog);
        const errorData: SimpleDialogProps = {
          title: "エラーが発生しました",
          content: `<span>作品の複製に失敗しました。</span><br /><span>ご迷惑をおかけして申し訳ありません。</span><br />原因の解消に関しましては <a href="${process.env.VUE_APP_NOTION_HELP}" target="blank">ヘルプ</a> をご確認ください`,
          isError: true,
          width: 350,
        };
        errorDialog.show(errorData);
      } finally {
        this.$emit("changeLoading", false);
      }
    },
    importNovel() {
      this.toggleMenu();
      openManuscriptFilePicker(this.$refs.input as HTMLInputElement);
    },
    deleteNovel() {
      const { novel } = this;
      if (!novel) return;
      this.toggleMenu();
      this.showNovelDeleteConfirmDialog(novel.title);
    },
    async showNovelDeleteConfirmDialog(title) {
      const { novel, novelIdFromNolaNovel } = this;
      const confirmDialog = new Dialog(NovelDeleteConfirm);
      const data: NovelDeleteConfirmProps = {
        novelId: novel.novelId,
        nolaNovelId: novelIdFromNolaNovel,
        title,
      };
      const result = await confirmDialog.show(data);
      if (result) {
        this.$notify({
          title: "削除しました",
          type: "error",
        });
      }
    },
    async showGroupingDialog() {
      this.toggleMenu();

      try {
        await this.$store.dispatch("novelGroupModule/initialize");
      } catch (e) {
        // 初期化失敗時もダイアログは表示する
      }

      const currentGroupIds = this.novel.groups?.map((g) => g.groupId) || [];
      const groupingDialog = new Dialog(NovelGroupingDialog);
      const result = await groupingDialog.show({
        novelId: this.novel.novelId,
        currentGroupIds,
      });

      if (result) {
        try {
          await this.$store.dispatch("novelModule/initialize");
          await this.$store.dispatch("novelGroupModule/initialize");
        } catch (e) {
          // 再取得失敗時は次回の画面表示時に再取得される
        }
      }
    },
    toggleMenu() {
      this.$emit("toggle-menu-click", this.novel.novelId);
    },
    async onSelectFile() {
      const fileList = (this.$refs.input as HTMLInputElement).files;
      if (fileList) {
        // サポートしているファイル形式（.txt / .docx）以外・Word 一時ファイルを除外
        const supportedFileList = filterSupportedManuscriptFiles(Array.from(fileList));

        // 対応形式のファイルが1件も無い場合は案内する（フォルダ内全選択などで発生しうる）
        if (supportedFileList.length === 0) {
          const noFileDialog = new Dialog(SimpleDialog);
          const noFileData: SimpleDialogProps = {
            title: this.$t("homePage.novelList.import.errorTitle") as string,
            content: this.$t("homePage.novelList.import.noSupportedFileMessage") as string,
            isError: true,
            width: 350,
          };
          noFileDialog.show(noFileData);
          this.toggleMenu();
          return;
        }

        // 取り込み先の作品が脚本モードなら、脚本用の整形を行う。
        const isScriptMode = this.novel.manuscriptMode === "script";

        try {
          this.$emit("changeLoading", true);
          const payloads = await readManuscriptFiles(supportedFileList, isScriptMode);
          await this.createMultipleManuscript(payloads);

          const completeDialog = new Dialog(SimpleDialog);
          const completeData: SimpleDialogProps = {
            title: this.$t("homePage.novelList.import.successTitle") as string,
            content: this.$t("homePage.novelList.import.successMessage") as string,
            width: 350,
          };
          completeDialog.show(completeData);
        } catch (error) {
          console.error(error);
          const errorDialog = new Dialog(SimpleDialog);
          const errorData: SimpleDialogProps = {
            title: this.$t("homePage.novelList.import.errorTitle") as string,
            content: this.$t("homePage.novelList.import.errorMessage") as string,
            isError: true,
            width: 350,
          };
          errorDialog.show(errorData);
        } finally {
          this.$emit("changeLoading", false);
          this.toggleMenu();
        }
      }
    },
    async createMultipleManuscript(payload) {
      const params = payload.map((item) => ({
        novelId: this.novel.novelId,
        title: item.title || "",
        content: item.content || "",
        manuscriptFolderId: null,
      }));
      await this.$store.dispatch("manuscriptModule/createMultipleManuscript", { params });
    },
  },
});

export default novelItemMixin;
